import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * Popup shown right after "Add to cart" with recommended products, each
 * pre-selected via a checkbox. "Add all to cart" bulk-adds every checked item.
 *
 * @typedef {object} PostAddToCartPopupRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} [addAllButton]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {DialogComponent}
 */
class PostAddToCartPopup extends DialogComponent {
  /** @type {boolean} */
  #addingFromSelf = false;

  /** @type {boolean} */
  #arrowsBound = false;

  /** @type {ResizeObserver | null} */
  #gridResizeObserver = null;

  #onCartUpdate = (event) => {
    if (this.#addingFromSelf) {
      this.#addingFromSelf = false;
      return;
    }

    if (event.detail?.data?.didError) return;

    // Quantity changes and removals in the cart drawer/page dispatch this same event
    // (source: 'cart-items-component', same guard cart-drawer.js uses for its own
    // auto-open) — only genuine add-to-cart actions should trigger this popup.
    if (event.detail?.data?.source === 'cart-items-component') return;

    const { resource } = event.detail;
    // Two resource shapes reach this handler: the full /cart.js cart (resource.items[]),
    // dispatched by our own product-form-component, and a single /cart/add.js line item
    // (resource.product_id directly) — dispatched by Bloomreach's search-widget.js for
    // the search results page's own "Add to cart" buttons.
    const productId = (
      event.detail.data?.productId ||
      resource?.items?.[0]?.product_id ||
      resource?.product_id
    )?.toString();
    if (!productId) return;

    const cartItem = resource?.items?.find((item) => item.product_id?.toString() === productId);
    const productPrice = cartItem ? cartItem.price / 100 : resource?.product_id ? (resource.price ?? 0) / 100 : 0;

    this.#load(productId, productPrice);
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // cart-drawer.js defers its own auto-open to this popup whenever it's present, so this
    // popup owns opening the cart afterward — whenever it closes, for any reason (dismissed,
    // Escape, backdrop click, or after a successful add from #addSingleItem/addSelectedToCart).
    this.addEventListener(DialogCloseEvent.eventName, this.#openCartDrawer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener(DialogCloseEvent.eventName, this.#openCartDrawer);
    this.#gridResizeObserver?.disconnect();
  }

  #openCartDrawer = () => {
    document.querySelector('cart-drawer-component')?.open?.();
  };

  /**
   * @param {string} productId
   * @param {number} productPrice
   */
  async #load(productId, productPrice) {
    try {
      const grid = await this.#loadFromBloomreach(productId, productPrice);

      console.log('[post-add-to-cart-popup] grid built', grid, grid?.children.length ?? 0, 'cards');

      if (!grid || !grid.children.length) {
        // No recommendations to show — cart-drawer.js deferred its auto-open to us, so
        // open it now instead of leaving the user with neither drawer nor popup.
        this.#openCartDrawer();
        return;
      }

      const { content } = this.refs;
      content.replaceChildren(grid);
      this.#bindArrows(grid);

      // cart-drawer.js deferred its auto-open to us — show recs first instead, and open
      // the cart once this dialog closes (see the DialogCloseEvent listener above).
      this.showDialog();
    } catch (error) {
      console.error('[post-add-to-cart-popup] failed to load', error);
      this.#openCartDrawer();
    }
  }

  /**
   * Fetches recommendations from Bloomreach Discovery, seeded with the added product and
   * filtered to complementary (cheaper accessory-style), in-stock items — mirrors the
   * price-band catalogFilter from the Bloomreach cross-sell popup spec, plus a stock_level
   * filter so out-of-stock items (a poor experience in a "buy more" popup) aren't recommended.
   * @param {string} productId
   * @param {number} productPrice
   * @returns {Promise<HTMLDivElement | null>}
   */
  #loadFromBloomreach(productId, productPrice) {
    const recommendationId = this.dataset.recommendationId;

    console.log('[post-add-to-cart-popup] loadFromBloomreach', {
      productId,
      productPrice,
      recommendationId,
      brwebAvailable: typeof brweb !== 'undefined',
    });

    if (typeof brweb === 'undefined' || !recommendationId) return Promise.resolve(null);

    const limit = Number(this.dataset.limit) || 8;

    // Mwave's catalog doesn't carry stock_level (it uses inventory_level + inventory_policy,
    // where CONTINUE items can be sellable at 0), so a stock_level filter there would
    // exclude everything. Stock is checked per item in #isInStock instead.
    const catalogFilter = this.#isMwave
      ? []
      : [
          {
            property: 'stock_level',
            constraint: {
              type: 'number',
              operator: 'greater than',
              operands: [{ type: 'constant', value: '0' }],
            },
          },
        ];

    if (productPrice) {
      const priceThreshold = (Number(this.dataset.priceThreshold) || 30) / 100;
      catalogFilter.push({
        property: 'price',
        constraint: {
          type: 'number',
          operator: 'in between',
          operands: [
            { type: 'constant', value: '0' },
            { type: 'constant', value: String(productPrice * priceThreshold) },
          ],
        },
      });
    }

    console.log('[post-add-to-cart-popup] catalogFilter', JSON.stringify(catalogFilter, null, 2));

    return new Promise((resolve) => {
      brweb.getRecommendation({
        recommendationId,
        size: limit,
        items: { [productId]: 1 },
        fillWithRandom: true,
        ...(catalogFilter.length ? { catalogFilter } : {}),
        callback: async (data) => {
          console.log('[post-add-to-cart-popup] brweb.getRecommendation response', data);

          if (!data || !data.length) {
            resolve(null);
            return;
          }
          resolve(await this.#buildBloomreachGrid(data));
        },
      });
    });
  }

  /**
   * The Bloomreach catalog carries everything needed to render a native-looking card
   * (title, image, price, compare-at price, promo badge, brand, stock) — it just
   * doesn't carry a Shopify variant ID, so resolve that separately per item via handle.
   * @param {Array<object>} items
   * @returns {Promise<HTMLDivElement>}
   */
  async #buildBloomreachGrid(items) {
    const grid = document.createElement('div');
    grid.className = 'post-add-to-cart-popup__grid';

    const cards = await Promise.all(
      items.map(async (item) => {
        // Belt-and-braces: the catalogFilter already excludes stock_level <= 0, but
        // Bloomreach's stock_level can lag real-time Shopify inventory — never show a
        // "Sold out" card in a popup whose whole purpose is encouraging more purchases.
        if (!this.#isInStock(item)) return null;
        // Custom PCs need configuring on the PDP — they can't be quick-added.
        if (this.#isMwave && this.#tagsString(item).includes('custom_pc')) return null;
        try {
          const variantId = await this.#resolveVariantId(item);
          if (!variantId) return null;
          return this.#renderCard(item, variantId);
        } catch {
          return null;
        }
      })
    );

    cards.filter(Boolean).forEach((card) => grid.appendChild(card));
    return grid;
  }

  get #isMwave() {
    return this.dataset.brand === 'mwave';
  }

  /**
   * @param {object} item
   * @returns {string}
   */
  #tagsString(item) {
    return Array.isArray(item.tags) ? item.tags.join('###') : item.tags || '';
  }

  /**
   * Mirrors extras/bloomreach-loomi/product-card-mwave-engagement.html for Mwave
   * (inventory_policy CONTINUE or inventory_level > 0, pre-orders always sellable);
   * digiDirect keeps using stock_level.
   * @param {object} item
   * @returns {boolean}
   */
  #isInStock(item) {
    if (!this.#isMwave) return (item.stock_level ?? 0) > 0;
    if (item.pre_order_status === 'Yes') return true;
    if (item.inventory_policy === 'CONTINUE') return true;
    const inventory = parseInt(item.inventory_level ?? item.stock_level, 10);
    return Number.isFinite(inventory) && inventory > 0;
  }

  /**
   * @param {{handle?: string, url: string}} item
   * @returns {Promise<number | null>}
   */
  async #resolveVariantId(item) {
    const handle = item.handle || item.url?.split('/products/')[1]?.split('?')[0];
    if (!handle) return null;
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) return null;
    const product = await response.json();
    return product.variants?.find((variant) => variant.available)?.id ?? product.variants?.[0]?.id ?? null;
  }

  /**
   * Renders a card matching extras/bloomreach-loomi/product-card-digi-engagement.html —
   * the field-verified Bloomreach engagement card template used elsewhere on the site —
   * so recommendations here look like every other Bloomreach-powered card, not a bespoke
   * layout. The wishlist heart button from that template is replaced with our selection
   * checkbox in the same top-right slot.
   * @param {object} item
   * @param {number} variantId
   * @returns {HTMLDivElement}
   */
  #renderCard(item, variantId) {
    const title = item['main.title'] || item.title;
    const price = Number(item.price) || 0;
    const inStock = this.#isInStock(item);
    const isPreOrder = this.#isMwave && item.pre_order_status === 'Yes';

    // The native product-card badge logic (snippets/product-card-badges.liquid) trusts
    // metafields.custom.is_digideals/is_digiprint for these, but Bloomreach's catalog
    // feed doesn't reliably sync those booleans — items that are genuinely in the
    // digiDeals/digiPrint collections (confirmed via their tags) can arrive with no
    // is_digideals/is_digiprint field at all. Check tags first, same as digiSeconds.
    const tagsStr = this.#tagsString(item);
    let digiBadge = null;
    if (tagsStr.includes('Category_Default Category/digiSeconds')) digiBadge = 'digiSeconds';
    else if (this.#isMwave) digiBadge = null;
    else if (tagsStr.includes('Category_Default Category/digiDeals') || item.is_digideals) digiBadge = 'digiDeals';
    else if (tagsStr.includes('Category_Default Category/digiPrint') || item.is_digiprint) digiBadge = 'digiPrint';

    const card = document.createElement('div');
    card.className = 'br-product-card post-add-to-cart-popup__card';

    const safeUrl = escapeHtml(item.url);
    const safeTitle = escapeHtml(title);
    const safeImage = escapeHtml(item.image || item.featuredImage?.url || item.thumb_image || '');
    const safeBrand = escapeHtml(item.brand || '');
    const selectLabel = escapeHtml((this.dataset.selectLabel || 'Select __PRODUCT__').replace('__PRODUCT__', title));
    const addToCartLabel = escapeHtml(this.dataset.addToCartLabel || 'Add to cart');
    const soldOutLabel = escapeHtml(this.dataset.soldOutLabel || 'Sold out');
    const preOrderLabel = escapeHtml(this.dataset.preOrderLabel || 'Pre-order');
    const formatMoney = (amount) =>
      typeof Shopify !== 'undefined' && Shopify.formatMoney ? Shopify.formatMoney(amount * 100) : `$${amount.toFixed(2)}`;

    const badgeHtml = digiBadge
      ? `<div class="br-product-card-badges">
           <span class="br-product-card-badges__pill br-product-card-badges__pill--${digiBadge.toLowerCase()}">
             <strong style="color: var(--color-brand-accent);">digi</strong>${escapeHtml(digiBadge.slice(4))}
           </span>
         </div>`
      : '';

    const promoHtml = item.promo_text
      ? `<div class="br-product-card__promo-text"><span>${escapeHtml(item.promo_text)}</span></div>`
      : '';

    const priceClass = item.promo_text ? 'br-product-card__price br-product-card__price--promo' : 'br-product-card__price';
    const priceHtml = `<div class="${priceClass}"><span>${formatMoney(price)}</span></div>`;

    // Matches snippets/product-card.liquid's BNPL row: digiPrint items get "Order online
    // today!" instead, and the whole line is gated by the same settings.show_bnpl_text
    // toggle native cards respect.
    // Mwave cards show supplier stock + free shipping, as in product-card-mwave-engagement.html.
    let stockShippingHtml = '';
    if (this.#isMwave) {
      const parts = [];
      // Pre-orders aren't in stock anywhere yet, so skip the stock line (MWA-131).
      const supplierMessage = isPreOrder ? null : item.supplier_message;
      if (supplierMessage === 'store') parts.push(`<span class="br-product-card__in-stock">${escapeHtml(this.dataset.inStockStoreLabel || 'In stock at Mwave')}</span>`);
      else if (supplierMessage === 'supplier') parts.push(`<span class="br-product-card__in-stock">${escapeHtml(this.dataset.inStockSupplierLabel || 'Available at Supplier')}</span>`);
      if (item.isFreeShipping === true || item.isFreeShipping === 'true') parts.push(`<span class="br-product-card__free-shipping">${escapeHtml(this.dataset.freeShippingLabel || 'Free shipping')}</span>`);
      if (parts.length) stockShippingHtml = `<p class="br-product-card__stock-shipping">${parts.join('')}</p>`;
    }

    const isDigiprint = digiBadge === 'digiPrint';
    const bnplHtml =
      this.dataset.showBnpl === 'true' || isDigiprint
        ? `<p class="br-product-card__bnpl">${
            isDigiprint ? escapeHtml(this.dataset.orderOnlineLabel || 'Order online today!') : escapeHtml(this.dataset.bnplLabel || 'Buy now pay later options available')
          }</p>`
        : '';

    card.innerHTML = `
      <div class="br-product-card__content">
        <div class="br-product-card__image">
          <a href="${safeUrl}"><img src="${safeImage}" alt="${safeTitle}" loading="lazy"></a>
          <label class="product-card__selection">
            <span class="visually-hidden">${selectLabel}</span>
            <input type="checkbox" class="product-card__selection-checkbox" data-variant-id="${variantId}" data-price="${price}" ${inStock ? 'checked' : ''} ${inStock ? '' : 'disabled'}>
          </label>
          ${badgeHtml}
        </div>
        <div class="br-product-card__info">
          <a class="br-product-card__link" href="${safeUrl}">
            ${safeBrand ? `<h6 class="br-eng__reco-item__brand">${safeBrand}</h6>` : ''}
            <h3 class="br-product-card__title">${safeTitle}</h3>
            ${stockShippingHtml}
            <div class="br-product-card__row">
              <div class="br-product-card__price-group">
                ${promoHtml}
                ${priceHtml}
              </div>
              ${bnplHtml}
            </div>
          </a>
          <div class="br-product-card__actions">
            <button type="button" class="br-product-card__add-to-cart" data-variant-id="${variantId}" ${inStock ? '' : 'disabled'}>
              ${!inStock ? soldOutLabel : isPreOrder ? preOrderLabel : addToCartLabel}
            </button>
          </div>
        </div>
      </div>
    `;

    // Belt-and-braces: third-party scripts (e.g. Swym's site-wide wishlist auto-scanner,
    // which targets any a[href*="/products/"] link) have been seen mutating this card's
    // DOM after insertion in a way that clears the checkbox's initial checked state even
    // though the "checked" attribute is present — set the property explicitly too.
    const checkbox = /** @type {HTMLInputElement | null} */ (card.querySelector('.product-card__selection-checkbox'));
    if (checkbox) checkbox.checked = inStock;

    if (inStock) {
      card.querySelector('.br-product-card__add-to-cart')?.addEventListener('click', (event) => {
        this.#addSingleItem(variantId, /** @type {HTMLButtonElement} */ (event.currentTarget));
      });
    }

    return card;
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   */
  async #addSingleItem(variantId, button) {
    button.disabled = true;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) return;

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;

      this.#addingFromSelf = true;
      document.dispatchEvent(
        new CartAddEvent(cart, this.id, { source: 'post-add-to-cart-popup', itemCount: 1 })
      );
      this.closeDialog();
    } finally {
      button.disabled = false;
    }
  }

  /**
   * Wires the carousel arrows (mobile-only, see CSS) to scroll the grid and
   * toggles their visibility based on scroll position — mirrors
   * cart-drawer-recommendations.js's #bindArrows.
   * @param {HTMLDivElement} grid
   */
  #bindArrows(grid) {
    const { prevButton, nextButton } = this.refs;
    if (!prevButton || !nextButton) return;

    const update = () => {
      prevButton.hidden = grid.scrollLeft <= 0;
      nextButton.hidden = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
    };

    this.#gridResizeObserver?.disconnect();
    this.#gridResizeObserver = new ResizeObserver(update);
    this.#gridResizeObserver.observe(grid);
    grid.addEventListener('scroll', update, { passive: true });
    update();

    if (this.#arrowsBound) return;
    this.#arrowsBound = true;

    const scrollBy = (direction) => {
      const currentGrid = this.refs.content.firstElementChild;
      if (!currentGrid) return;
      const card = currentGrid.children[0];
      const cardWidth = card ? card.getBoundingClientRect().width : currentGrid.clientWidth;
      const gap = parseFloat(getComputedStyle(currentGrid).gap) || 0;
      currentGrid.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
    };

    prevButton.addEventListener('click', () => scrollBy(-1));
    nextButton.addEventListener('click', () => scrollBy(1));
  }

  /**
   * Bulk-adds every checked recommendation to the cart, then closes the popup.
   */
  addSelectedToCart = async () => {
    const { content, addAllButton, errorMessage } = this.refs;
    const checkboxes = /** @type {NodeListOf<HTMLInputElement>} */ (
      content.querySelectorAll('.product-card__selection-checkbox:checked')
    );

    const items = Array.from(checkboxes)
      .map((checkbox) => checkbox.dataset.variantId)
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));

    if (!items.length) {
      this.closeDialog();
      return;
    }

    if (errorMessage) errorMessage.textContent = '';
    if (addAllButton) addAllButton.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (errorMessage) errorMessage.textContent = data.message || data.description || '';
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;

      this.#addingFromSelf = true;
      document.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'post-add-to-cart-popup',
          itemCount: items.reduce((count, item) => count + item.quantity, 0),
        })
      );

      this.closeDialog();
    } catch {
      if (errorMessage) errorMessage.textContent = this.dataset.genericError || '';
    } finally {
      if (addAllButton) addAllButton.disabled = false;
    }
  };
}

if (!customElements.get('post-add-to-cart-popup')) {
  customElements.define('post-add-to-cart-popup', PostAddToCartPopup);
}
